/**
 * Alshallal Pastry Company HR System - OCR Status Badge Component
 * Phase 15: OCR-Assisted Document Data Extraction
 * 
 * Compact badge showing OCR status with link to document details.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Scan, Clock, CheckCircle, XCircle, RefreshCw, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const OCR_STATUS_CONFIG = {
  not_run: { 
    bg: 'bg-slate-100 text-slate-600', 
    icon: Scan, 
    label: { en: 'Not Run', ar: 'لم يتم' }
  },
  queued: { 
    bg: 'bg-slate-100 text-slate-700', 
    icon: Clock, 
    label: { en: 'Queued', ar: 'في الانتظار' }
  },
  processing: { 
    bg: 'bg-blue-100 text-blue-700', 
    icon: RefreshCw, 
    label: { en: 'Processing', ar: 'قيد المعالجة' },
    animate: true
  },
  completed: { 
    bg: 'bg-green-100 text-green-700', 
    icon: CheckCircle, 
    label: { en: 'Completed', ar: 'مكتمل' }
  },
  failed: { 
    bg: 'bg-red-100 text-red-700', 
    icon: XCircle, 
    label: { en: 'Failed', ar: 'فشل' }
  },
};

/**
 * OCRStatusBadge - Shows OCR status with optional link to document OCR tab
 * 
 * @param {string} status - OCR job status (not_run, queued, processing, completed, failed)
 * @param {string} documentId - Document ID for linking
 * @param {boolean} showLink - Whether to show link to OCR tab
 * @param {string} language - Current language (en/ar)
 * @param {string} size - Badge size (sm, default)
 */
const OCRStatusBadge = ({ 
  status = 'not_run', 
  documentId, 
  showLink = true, 
  language = 'en',
  size = 'default',
  pendingCount = 0
}) => {
  const config = OCR_STATUS_CONFIG[status] || OCR_STATUS_CONFIG.not_run;
  const StatusIcon = config.icon;
  const label = config.label[language] || config.label.en;

  const badgeContent = (
    <Badge 
      className={cn(
        config.bg,
        size === 'sm' ? 'text-xs py-0 px-1.5' : 'py-0.5 px-2',
        showLink && documentId && 'cursor-pointer hover:opacity-80'
      )}
    >
      <StatusIcon className={cn(
        size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
        'me-1',
        config.animate && 'animate-spin'
      )} />
      {label}
      {status === 'completed' && pendingCount > 0 && (
        <span className="ms-1 bg-amber-500 text-white rounded-full px-1.5 text-xs">
          {pendingCount}
        </span>
      )}
    </Badge>
  );

  if (showLink && documentId) {
    return (
      <Link 
        to={`/documents/${documentId}?tab=ocr`}
        title={language === 'ar' ? 'عرض نتائج OCR' : 'View OCR Results'}
      >
        {badgeContent}
      </Link>
    );
  }

  return badgeContent;
};

export default OCRStatusBadge;
